import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useServices } from '@/context/ServiceContext'
import { useAuth } from '@/context/AuthContext'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { domainEvents } from '@/services/events/domainEvents'
import { scoreQuiz } from '@/domain/quizScoring'
import { DEFAULT_PASSING_THRESHOLD_PERCENT } from '@/domain/constants'
import type { Episode } from '@/domain/episode'
import type { Question } from '@/domain/question'
import type { QuizResult } from '@/domain/result'
import type { QuizPassedPayload, QuizFailedPayload } from '@/services/gamification/events'

export function QuizPage() {
  const { id } = useParams<{ id: string }>()
  const { episodes, quiz, progress, results, organizations, badges, xp, certificateRepo } = useServices()
  const { currentUser } = useAuth()
  const navigate = useNavigate()

  const [episode, setEpisode] = useState<Episode | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [passingThreshold, setPassingThreshold] = useState(DEFAULT_PASSING_THRESHOLD_PERCENT)
  const [loading, setLoading] = useState(true)
  const [blockedReason, setBlockedReason] = useState<string | null>(null)

  const [currentIndex, setCurrentIndex] = useState(0)
  const [selections, setSelections] = useState<Record<string, string[]>>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!id || !currentUser) return
    let cancelled = false

    async function load() {
      const ep = await episodes.getById(id!)
      if (!ep) {
        if (!cancelled) {
          setBlockedReason('Episode not found.')
          setLoading(false)
        }
        return
      }
      const [prog, qs, org] = await Promise.all([
        progress.getForEpisode(currentUser!.id, id!),
        quiz.getQuestionsForEpisode(id!),
        currentUser!.orgId ? organizations.getById(currentUser!.orgId) : Promise.resolve(null),
      ])
      if (cancelled) return

      if (!prog?.completed) {
        setBlockedReason('Watch the full episode before taking its quiz.')
        setLoading(false)
        return
      }
      if (qs.length === 0) {
        setBlockedReason('This episode does not have a quiz yet.')
        setLoading(false)
        return
      }

      setEpisode(ep)
      setQuestions(qs)
      setPassingThreshold(org?.settings.passingThresholdPercent ?? DEFAULT_PASSING_THRESHOLD_PERCENT)
      domainEvents.emit('quiz.started', { episodeId: id, userId: currentUser!.id })
      setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [id, currentUser, episodes, quiz, progress, organizations])

  const currentQuestion = questions[currentIndex]
  const isLastQuestion = currentIndex === questions.length - 1
  const currentSelection = currentQuestion ? selections[currentQuestion.id] ?? [] : []
  const canAdvance = currentSelection.length > 0

  const toggleOption = (optionId: string) => {
    if (!currentQuestion) return
    const isMultiSelect = currentQuestion.type === 'multi_select'
    setSelections((prev) => {
      const existing = prev[currentQuestion.id] ?? []
      if (isMultiSelect) {
        const next = existing.includes(optionId)
          ? existing.filter((o) => o !== optionId)
          : [...existing, optionId]
        return { ...prev, [currentQuestion.id]: next }
      }
      return { ...prev, [currentQuestion.id]: [optionId] }
    })
  }

  const handleNext = () => {
    if (!canAdvance) return
    setCurrentIndex((i) => Math.min(i + 1, questions.length - 1))
  }
  const handleBack = () => setCurrentIndex((i) => Math.max(i - 1, 0))

  const handleSubmit = async () => {
    if (!currentUser || !episode || submitting) return
    setSubmitting(true)

    const scored = scoreQuiz(questions, selections)
    const passed = scored.scorePercent >= passingThreshold
    const attemptNumber = (await results.countAttempts(currentUser.id, episode.id)) + 1

    const result: QuizResult = {
      id: crypto.randomUUID(),
      userId: currentUser.id,
      orgId: currentUser.orgId,
      episodeId: episode.id,
      answers: scored.answers,
      scorePercent: scored.scorePercent,
      passed,
      attemptNumber,
      submittedAt: new Date().toISOString(),
    }
    await results.save(result)

    let freshlyAwardedBadgeKeys: string[] = []
    let xpEarnedThisAttempt = 0
    let freshCertificateId: string | null = null

    if (passed) {
      // Snapshot before/after rather than trusting timestamps — deterministic,
      // and needs no new repository methods beyond what already exists.
      const [badgesBefore, xpBefore, certsBefore] = await Promise.all([
        badges.listAwardsForUser(currentUser.id),
        xp.getTotalForUser(currentUser.id),
        certificateRepo.listForUser(currentUser.id),
      ])
      const badgeKeysBefore = new Set(badgesBefore.map((a) => a.badgeKey))
      const certIdsBefore = new Set(certsBefore.map((c) => c.id))

      const payload: QuizPassedPayload = {
        userId: currentUser.id,
        orgId: currentUser.orgId,
        episodeId: episode.id,
        resultId: result.id,
        scorePercent: result.scorePercent,
        attemptNumber,
      }
      // Awaited deliberately: GamificationService's quiz.passed handler
      // persists XP/badges/certificate before this resolves, so the
      // Results page (loaded immediately after navigate()) never races it.
      await domainEvents.emit('quiz.passed', payload)

      const [badgesAfter, xpAfter, certsAfter] = await Promise.all([
        badges.listAwardsForUser(currentUser.id),
        xp.getTotalForUser(currentUser.id),
        certificateRepo.listForUser(currentUser.id),
      ])
      freshlyAwardedBadgeKeys = badgesAfter
        .filter((a) => !badgeKeysBefore.has(a.badgeKey))
        .map((a) => a.badgeKey)
      xpEarnedThisAttempt = xpAfter - xpBefore
      freshCertificateId = certsAfter.find((c) => !certIdsBefore.has(c.id))?.id ?? null
    } else {
      const payload: QuizFailedPayload = {
        userId: currentUser.id,
        orgId: currentUser.orgId,
        episodeId: episode.id,
        resultId: result.id,
        scorePercent: result.scorePercent,
        attemptNumber,
      }
      await domainEvents.emit('quiz.failed', payload)
    }

    navigate(`/episodes/${episode.id}/quiz/results/${result.id}`, {
      state: { freshlyAwardedBadgeKeys, xpEarnedThisAttempt, freshCertificateId },
    })
  }

  const progressLabel = useMemo(
    () => (questions.length ? `Question ${currentIndex + 1} of ${questions.length}` : ''),
    [currentIndex, questions.length]
  )

  if (loading) return <LoadingSpinner label="Preparing your quiz…" />

  if (blockedReason || !episode || !currentQuestion) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-white">Quiz not available yet</h1>
        <p className="mt-3 text-white/60">{blockedReason ?? 'Something went wrong loading this quiz.'}</p>
        <Link to={id ? `/episodes/${id}` : '/episodes'} className="cyber-btn-primary mt-8 inline-flex">
          Back to Episode
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8">
      <Link to={`/episodes/${episode.id}`} className="text-sm text-white/50 hover:text-white">
        ← {episode.title}
      </Link>

      <div className="mt-4 flex items-center justify-between">
        <span className="cyber-pill text-teal-200">{progressLabel}</span>
        <span className="text-xs uppercase tracking-wide text-white/40">
          {currentQuestion.type === 'multi_select' ? 'Select all that apply' : 'Select one'}
        </span>
      </div>

      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-gradient-to-r from-teal-400 via-indigo-400 to-emerald-400 transition-all"
          style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
        />
      </div>

      <div className="cyber-panel mt-6 p-6">
        <h1 className="font-display text-xl font-semibold text-white">{currentQuestion.stem}</h1>

        <div className="mt-6 space-y-3">
          {currentQuestion.options.map((opt) => {
            const selected = currentSelection.includes(opt.id)
            const isMultiSelect = currentQuestion.type === 'multi_select'
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggleOption(opt.id)}
                className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition ${
                  selected
                    ? 'border-teal-400/60 bg-teal-500/10 text-white'
                    : 'border-white/10 bg-white/5 text-white/80 hover:border-white/25'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center border text-xs ${
                    isMultiSelect ? 'rounded-md' : 'rounded-full'
                  } ${selected ? 'border-teal-400 bg-teal-400 text-navy-950' : 'border-white/30'}`}
                >
                  {selected ? '✓' : ''}
                </span>
                {opt.text}
              </button>
            )
          })}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={currentIndex === 0}
          className="cyber-btn-secondary disabled:cursor-not-allowed disabled:opacity-40"
        >
          Back
        </button>
        {isLastQuestion ? (
          <button
            onClick={handleSubmit}
            disabled={!canAdvance || submitting}
            className="cyber-btn-primary disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submitting ? 'Submitting…' : 'Submit Quiz'}
          </button>
        ) : (
          <button
            onClick={handleNext}
            disabled={!canAdvance}
            className="cyber-btn-primary disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        )}
      </div>
    </div>
  )
}
